import type { WorkflowExecutionFailedType } from '../generated/enums';
import type { SelectorStrategiesDTO } from './selector-strategy.dto';
import { z } from 'zod/v3';

// ========== Zod Schemas for Runtime Validation ==========
// These schemas mirror the TypeScript interfaces below and are used for:
// 1. LLM structured output validation (compile-workflow-from-doc.ts)
// 2. Database storage validation (TemplateExecutionConfig)
// 3. API request/response validation

/**
 * Workflow Parameter Schema
 * Defines a variable parameter that can be passed to a workflow
 */
/** Workspace file references carried by Procedure startup inputs. */
export const RunbookInputFileSchema = z.object({
  id: z.string().min(1),
  filename: z.string().min(1),
  url: z.string().url().refine((value) => /^https?:\/\//.test(value)),
  mimeType: z.string(),
  size: z.number().int().nonnegative(),
});
export type RunbookInputFileDTO = z.infer<typeof RunbookInputFileSchema>;

export const WorkflowParamSchema = z.object({
  type: z.enum(['string', 'text', 'number', 'ordinal', 'boolean', 'url', 'email', 'date', 'enum', 'json', 'browserProfile', 'files']),
  value: z.unknown(),
  defaultValue: z.unknown().optional(),
  description: z.string(),
  required: z.boolean().default(true),
  secret: z.boolean().optional(),
  sensitive: z.boolean().optional(),
  displayOrder: z.number().int().nonnegative().optional(),
  validation: z
    .object({
      pattern: z.string().optional(),
      min: z.number().optional(),
      max: z.number().optional(),
      enum: z.array(z.string()).optional(),
    })
    .optional(),
});

/**
 * Step Extraction Schema
 * Defines what data to extract from a page element
 */
export const StepExtractionSchema = z.object({
  name: z.string().describe('Output variable name (e.g., "issueTitle", "os")'),
  selector: z.string().optional().describe('Specific selector for this extraction (overrides step hints)'),
  xpath: z.string().optional(),
  extractField: z.enum(['textContent', 'value', 'href', 'src', 'innerText', 'innerHTML', 'attribute']).optional(),
  description: z.string().optional().describe('What this extraction represents'),
});

/**
 * Step Hints Schema
 * Provides selector strategies and additional context for step execution
 */
export const StepHintsSchema = z.object({
  // Selector strategies
  selector: z.string().optional(),
  xpath: z.string().optional(),
  testId: z.string().optional(),
  ariaLabel: z.string().optional(),

  // Element identification (CRITICAL for Fast Path validation)
  domText: z.string().optional().describe('CRITICAL: Expected text content for validation. Use {{param}} for parameterized values. Required for click/type actions with parameters.'),
  ordinality: z.string().optional().describe('CRITICAL: Position in list (first, last, 2, {{position}}). Required for selecting items in lists.'),
  containerSelector: z.string().optional().describe('Container to constrain search'),

  // Validation
  fingerprint: z.object({
    id: z.string().optional(),
    href: z.string().optional(),
    textContent: z.string().optional(),
    tagName: z.string().optional(),
  }).optional(),

  // Navigation hints
  url: z.string().optional(),
  urlPattern: z.string().optional(),

  // Type hints
  value: z.string().optional(),
  clearFirst: z.boolean().optional(),

  // Extract hints
  extractField: z.enum(['textContent', 'value', 'href', 'src', 'innerText']).optional(),

  // Wait hints
  waitFor: z.object({
    type: z.enum(['url', 'element', 'text', 'networkIdle']),
    value: z.string().optional(),
    timeout: z.number().optional(),
  }).optional(),
});

/**
 * Observed State Schema
 * Signals captured during recording for validation
 */
export const ObservedStateSchema = z.object({
  errorElements: z.array(z.object({
    text: z.string(),
    selector: z.string(),
    color: z.string().optional(),
    role: z.string().optional(),
  })).optional(),
  urlChanged: z.boolean().optional(),
  modalAppeared: z.boolean().optional(),
  httpStatus: z.number().optional(),
});

/**
 * Step Validation Policy Schema
 * Optional contract to guide step success/failure validation.
 * Backward-compatible: when omitted, runtime derives policy from action + intent.
 */
export const StepValidationPolicySchema = z.object({
  mode: z.enum(['auto', 'dom', 'visual', 'hybrid']).optional(),
  intentType: z
    .enum([
      'focus_input',
      'open_panel',
      'close_panel',
      'toggle_on',
      'toggle_off',
      'submit_form',
      'navigate',
      'observe_only',
      'input_value',
      'select_option',
      'generic_click',
    ])
    .optional(),
  authFlow: z.enum(['none', 'login', 'logout', 'switch_user', 'unknown']).optional(),
  sessionExpiryCheck: z.enum(['auto', 'strict', 'off']).optional(),
  authFlowSource: z.enum(['llm', 'heuristic', 'manual']).optional(),
  authFlowConfidence: z.number().min(0).max(1).optional(),
  requiredSignals: z.array(z.string()).optional(),
  optionalSignals: z.array(z.string()).optional(),
  failureSignals: z.array(z.string()).optional(),
  strictness: z.enum(['strict', 'balanced', 'lenient']).optional(),
});

/**
 * Workflow Step Schema
 * Complete definition of an executable workflow step
 */
export const WorkflowStepSchema = z.object({
  id: z.string(),
  intent: z.string().describe('What this step accomplishes'),
  action: z.enum(['navigate', 'click', 'type', 'extract', 'wait', 'scroll', 'hover', 'select']),
  prompt: z.string().describe('Natural language instruction with @param references'),
  hints: StepHintsSchema,
  output: z.string().optional().describe('DEPRECATED: Use extractions array instead. Single output variable name'),

  // Multi-value extraction support
  extractions: z.array(StepExtractionSchema).optional().describe('Multiple values to extract from this step. Use this instead of output for extract actions.'),

  // Natural language success criteria
  successCriteria: z.string().optional().describe('Natural language description of expected outcome (e.g., "login should fail", "extract the title")'),

  // Observed state from recording
  observedState: ObservedStateSchema.optional().describe('Signals captured during recording'),

  // Optional validation policy (intent + signal contract)
  validation: StepValidationPolicySchema.optional().describe('Optional step-level validation policy'),
});

/**
 * Workflow Param Entry Schema
 * Key-value pair representation for array form (Gemini-compatible)
 */
export const WorkflowParamEntrySchema = z.object({
  name: z.string().describe('Parameter name (without @ prefix)'),
  param: WorkflowParamSchema,
});

/**
 * Workflow Output Schema
 * Expected outputs from workflow execution
 */
export const WorkflowOutputSchema = z.object({
  name: z.string().describe('Output variable name'),
  type: z.enum(['string', 'number', 'boolean', 'object', 'array']),
  required: z.boolean().default(false),
  description: z.string(),
  extractedFrom: z.string().optional().describe('Step ID that produces this output'),
});

/**
 * Compilation Result Schema
 * Complete workflow structure returned by LLM compilation
 */
export const CompilationResultSchema = z.object({
  confidence: z.number().min(0).max(1).describe('Confidence score 0.0-1.0'),
  summary: z.string().optional().describe('2-3 sentence summary'),
  params: z.array(WorkflowParamEntrySchema).optional().describe('Variable parameters with @param syntax'),
  steps: z.array(WorkflowStepSchema).describe('Executable workflow steps'),

  // Workflow-level success criteria
  outputs: z.array(WorkflowOutputSchema).optional().describe('Expected outputs from workflow execution'),
  successCriteria: z.string().describe('REQUIRED: Natural language description of workflow success, based on the last step content'),
});

// ========== TypeScript Interfaces ==========

/**
 * Workflow DTOs for DeepGuide Agent Reliability Layer
 * Based on workflow-language-spec.md
 *
 * These DTOs define the standardized JSON format for workflows that can be:
 * 1. Compiled from LLM (from documentation)
 * 2. Edited visually using ReactFlow
 * 3. Executed by the DeepGuide execution engine
 */

// ========== Core Workflow Types ==========

export interface WorkflowDTO {
  id: string;
  workspaceId: string;
  name: string;
  description?: string;
  version: number;

  params: Record<string, WorkflowParamDTO>;
  steps: WorkflowStepDTO[];
  documentation?: WorkflowDocumentationDTO;
  metadata: WorkflowMetadataDTO;

  // Workflow-level success criteria (NEW)
  outputs?: WorkflowOutputDTO[];
  successCriteria?: string; // Natural language description

  createdAt: string;
  updatedAt: string;
  compiledBy?: 'llm' | 'human' | 'hybrid';
}

export type RunbookKind = 'AUTOMATION' | 'PROCEDURE' | 'MIXED';

export type RunbookCategory =
  | 'automation'
  | 'guided'
  | 'mixed'
  | 'screenshot'
  | 'attestation';

/**
 * RFC-167 §5.7.2: canonical declared input on a Runbook. Replaces the
 * legacy `WorkflowParamDTO` (kept as a deprecated alias for back-compat
 * with the workflow-editor surfaces being sunset).
 */
export interface RunbookParamDTO {
  /** Input display position, preserved independently of JSONB key order. */
  displayOrder?: number
  type: ParamType
  value: any
  description: string
  required?: boolean
  validation?: ParamValidationDTO
  defaultValue?: unknown
  secret?: boolean
  sensitive?: boolean
  /** RFC-210: include this declared param in the approval/fulfillment hash. */
  decisionMaterial?: boolean
}

export interface RunbookRedactedValueDTO {
  redacted: true;
  maskedPreview: string;
  fingerprint: string;
  source?: string;
}

export type RunbookStepKind =
  | 'automated'
  // The consolidated human step: a composable evidence form (see
  // RunbookHumanFormDTO / HumanStepFormEditor). Supersedes the former
  // review/upload/attest/assisted kinds. The Postgres `StepKind` enum keeps
  // those values as inert tombstones (it has no DROP VALUE), but no code
  // produces or consumes them anymore.
  | 'human'
  // RFC-194 §8.2 (F1): a side-effect step the executor performs on reach and
  // auto-completes — dispatch a Slack/Email/Teams notification. NOT human-gated,
  // NOT evidence, NOT assignable; excluded from the human inbox + overdue sweep.
  | 'notify';

/**
 * RFC-167 — implicit, read-only outputs a *human* step produces at runtime,
 * referenceable downstream as `@step.<id>.<name>` even though human steps have
 * no Advanced/outputVar authoring field. The executor emits these keys on
 * completion (Upload → file ids, Approve/Reject → the decision, Sign off → the
 * signed statement); the save path mirrors them into `outputVarNames` so the
 * refs validate + resolve. Automatic steps name their own outputs instead.
 */
export const RUNBOOK_HUMAN_STEP_OUTPUTS: Partial<
  Record<RunbookStepKind, ReadonlyArray<{ name: string; type: string }>>
> = {
  // A `human` step's outputs depend on its form fields (resolved per-step from
  // RunbookHumanFormDTO via humanStepOutputs()), so there are no static entries.
};

export type RunbookStepRunner = 'api' | 'tool' | 'browser';

export interface RunbookDependencyDTO {
  id: string;
  fromStepId: string;
  toStepId: string;
  sourceOutputKey?: string | null;
  targetInputKey?: string | null;
  /**
   * RFC-167 §13.6 — edge condition for minimal pass/fail branching.
   * `success` (default) = traverse when `fromStepId` passes; `failure` =
   * traverse when it fails. The executor follows `failure` edges instead of
   * halting only when the failed step has one; success-only graphs (every
   * runbook today) are unaffected.
   */
  condition?: 'success' | 'failure';
}

export type StepRunStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'AWAITING_HUMAN'
  | 'COMPLETED'
  | 'FAILED'
  | 'SKIPPED';

export type WorkflowRunStatus =
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'PARTIAL'
  | 'AWAITING_HUMAN'
  | 'CANCELLED';

export interface RunbookActionDefinitionDTO {
  provider: string;
  action: string;
  params: Record<string, unknown>;
  outputVar?: string;
  checkId?: string;
  tool?: string;
  label: string;
  requiresHumanAction?: boolean;
  /**
   * Optional explicit binding to a specific IntegrationConnection. When
   * set, the executor MUST use this connection or fail with a clear
   * "connection unavailable" error. When unset, the executor falls back
   * to the run-level connectionId, then to the workspace's primary
   * connection for the provider — the legacy behavior.
   *
   * Authored at design time on the action pill (RFC #323) so audit
   * trails can attribute "the user list was pulled from production-aws"
   * deterministically rather than "from whichever connection the runner
   * happened to pick."
   */
  connectionId?: string;
}

export interface RunbookAutomatedStepPayloadDTO {
  runner: RunbookStepRunner;
  actions: RunbookActionDefinitionDTO[];
  browser?: RunbookBrowserPayloadDTO;
  /**
   * RFC-167 M2 — step-level browser login identity (a `BrowserProfile.id`),
   * overriding the program-level profile for THIS `runner:browser` step.
   * DESIGN HOOK ONLY — not yet honored by the executor (which acquires a single
   * program-level session per run). When implemented, the executor pre-scans
   * steps, groups them by effective `steelProfileId` (step `profileRef` ??
   * program `browserProfileId`), and lazily opens one session per group so a
   * single run can touch multiple logged-in systems.
   */
  profileRef?: string;
}

/**
 * RFC-167 §5.7.5: A reference to a value resolvable at run time.
 * Stored form: `{ $ref: '@step.s_foo.awsEmployees' }` etc.
 */
export interface VarRef {
  $ref: string;
}

/**
 * RFC-167 §5.7.5: A step input — either an inline JSON literal,
 * a single ref, or a templated string with embedded refs.
 */
export type StepInput =
  | string
  | number
  | boolean
  | null
  | StepInput[]
  | { [key: string]: StepInput }
  | VarRef
  | { $template: string };

/**
 * RFC-167 §5.7.3: Declared output contract per step.
 *
 * `OutputSpec` is the canonical shape with explicit key — used in arrays.
 * `RunbookStepOutputDTO` is the same contract minus `key`, used as the
 * value type when outputs are stored as `Record<key, RunbookStepOutputDTO>`
 * on `RunbookStepDTO.outputs`. `'unknown'` is permitted while existing
 * runbooks migrate from inferred-type compilation; new authoring should
 * use one of the typed variants.
 */
export type RunbookOutputType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'object'
  | 'array'
  | 'artifact'
  | 'unknown';

export interface RunbookStepOutputDTO {
  type: RunbookOutputType;
  description?: string;
  itemShape?: unknown; // JSONSchema (pinned loose to avoid pulling json-schema typings into shared package)
  sensitive?: boolean;
}

/**
 * An author-declared runbook output — the runbook's typed "return value", one
 * named field mapped to an upstream variable ref (Dify End-node style). Resolved
 * at run completion into `WorkflowExecution.outputs[name]`. The set of these is
 * the runbook's function signature; future consumers (sub-runbook composition,
 * structured results into a CES) read it. `name` is the return key; `ref` is a
 * canonical `@step.<id>.<field>` / `@params.<name>` / `@run.<field>` reference.
 */
export interface RunbookOutputDeclarationDTO {
  /** Stable row id — React key + reorder; not the return key. */
  id: string;
  /** The return-value field name (e.g. `result`, `discrepancyCsv`). */
  name: string;
  /** Canonical ref to the source variable (`@step.review.csv`, `@params.x`). */
  ref: string;
  /** Optional declared type; defaults to the referenced output's type. */
  type?: RunbookOutputType;
}

export interface OutputSpec extends RunbookStepOutputDTO {
  key: string;
  type: Exclude<RunbookOutputType, 'unknown'>;
}

export interface RunbookBrowserPayloadDTO {
  action: StepAction;
  prompt: string;
  hints?: StepHintsDTO;
  extractions?: StepExtractionDTO[];
  pagination?: StepPaginationConfigDTO;
  selectorStrategies?: SelectorStrategiesDTO;
  /**
   * RFC-167 M2 — the runbook definition step id. Threaded to the engine so
   * Layer-1 recording matching prefers stepId (stronger than action-matching)
   * and so a healed selector can be persisted back to the right step.
   */
  stepId?: string;
}

/**
 * The composable form a `human` step presents to its operator — the
 * consolidation of the former review/upload/attest/assisted kinds into one
 * "provide evidence" form (see HumanStepFormEditor). A step's form is a
 * read-only context block (`displayData`) plus an ordered list of input
 * `fields` the operator fills in; every field value is captured as evidence.
 */
export type RunbookHumanFieldType =
  // Free text — covers a conclusion, notes, a reply to a reporter, or a
  // sign-off statement (with `sign: true`).
  | 'text'
  // File attachments. ALWAYS multi-upload — an operator can attach any number
  // of files; `required` only governs whether at least one is needed.
  | 'files'
  // A single number (rendered as a numeric input). Stored as a string value.
  | 'number'
  // A calendar date (rendered as a date picker, captured as ISO `YYYY-MM-DD`).
  | 'date'
  // A URL (rendered as a URL input, lightly validated by the browser).
  | 'url'
  // A single choice from an author-defined option list (rendered as a dropdown,
  // captured as the chosen option's `value` string). Unlike a decision gate, a
  // `select` does NOT branch the run — it is plain evidence, and a step may have
  // several. Covers yes/no (options `Yes`/`No`) and any other enumerated answer.
  | 'select'
  // A browser recording the operator captures at this step (via the recorder
  // extension). Bound to the StepRun; its screenshots + signed manifest become
  // that step's evidence (materialized into the step folder at attach).
  | 'browser_recording';

/** A single choice on a `select` field. `value` is the stable captured value;
 *  `label` is what the operator sees. (No `tone`/edge — a select never forks.) */
export interface RunbookHumanFieldOptionDTO {
  value: string;
  label: string;
}

export interface RunbookHumanFormFieldDTO {
  id: string;
  label: string;
  type: RunbookHumanFieldType;
  /** Whether the operator must fill this in. A `decision` field is always required. */
  required: boolean;
  /** Optional helper text shown beneath the field at run time. */
  help?: string;
  /**
   * `select` only — the choices the operator picks from. The captured value is
   * the chosen option's `value`. Ignored (and cleared by the factory) on every
   * other field type.
   */
  options?: RunbookHumanFieldOptionDTO[];
  /**
   * `text` only — marks this as a written sign-off / signature field (the
   * "I confirm…" affirmation). Today it captures the typed statement and renders
   * a sign-off label; richer written-signature capture is a future enhancement.
   *
   * It is NOT a per-field digital signature: the run's integrity is provided by
   * the workflow's evidence signing (OES) over the whole execution. Completing a
   * non-empty sign-off records its statement and server-attributed signer/time on
   * `StepRun.attestationSignature`; it does not create a second cryptographic
   * signature.
   */
  sign?: boolean;
}

/**
 * A read-only value surfaced to the operator at run time so they have the
 * context they need to act (mirrors sim.ai's "Display Data"). `value` supports
 * `@params.<name>` and `@step.<id>.<output>` interpolation, resolved when the
 * step is reached.
 */
export interface RunbookHumanFormDisplayDTO {
  id: string;
  label: string;
  value: string;
}

/**
 * A single selectable outcome on a step's decision gate. `key` is stable and
 * powers both the captured value (`@step.<id>.<decisionId>` === the chosen key)
 * and the edge mapping. Today only the Approve/Reject preset ships
 * (keys `'approve' | 'reject'` → the step's success/failure edges); the list
 * shape lets this grow to N labelled outcomes (each with its own out-edge)
 * without reshaping saved runbooks.
 */
export interface RunbookDecisionOptionDTO {
  key: string;
  label: string;
  /** Styling hint for the run-time control (approve = positive, reject = negative). */
  tone?: 'positive' | 'negative';
}

/**
 * A step-level decision gate on a `human` step's form — the *fork*. Supersedes
 * the former `decision` field type: a decision is 0..1 per step and drives the
 * step's branch, so it belongs on the form, not in `fields`. When present, the
 * operator picks one outcome (and optionally a reason) and the run follows the
 * matching edge.
 */
export interface RunbookDecisionDTO {
  /**
   * Stable id; base of the decision's implicit run outputs — the chosen key is
   * exposed as `@step.<id>.<decisionId>` and the reason as
   * `@step.<id>.<decisionId>_reason`.
   */
  id: string;
  /** The question shown to the operator (e.g. "Is this report valid?"). */
  label: string;
  /** Optional helper text shown beneath the control at run time. */
  help?: string;
  /** Require the operator to enter a reason; default optional. */
  reasonRequired?: boolean;
  /** Selectable outcomes; today the 2-entry Approve/Reject preset. */
  options: RunbookDecisionOptionDTO[];
}

export interface RunbookHumanFormDTO {
  /** Read-only context shown to the operator (may be empty). */
  displayData: RunbookHumanFormDisplayDTO[];
  /** Input fields the operator fills in; their values are saved as evidence. */
  fields: RunbookHumanFormFieldDTO[];
  /**
   * Optional decision gate (RFC-167 §13.6). When present the step forks:
   * `approve` → the success edge, `reject` → the failure edge. Captured as a
   * run output alongside its reason. Modelled as a list of options so it can
   * grow to N labelled outcomes later.
   */
  decision?: RunbookDecisionDTO;
}

/**
 * RFC-194 §8.2 (F1) — `notify` step payload. The executor dispatches this on
 * reaching the step, then auto-completes. Lifecycle timing is expressed by the
 * node's *position* in the graph (a notify node on a failure edge = "on
 * failure"; at the tail = "on complete"), not by an enum.
 */
export interface RunbookNotifyStepPayloadDTO {
  channel: 'slack' | 'email' | 'teams';
  /**
   * Delivery target — the **To** recipient(s). An email address or
   * comma-separated addresses; supports `@params.<name>` interpolation. Legacy
   * runbooks may still hold a Slack/Teams incoming-webhook URL. The
   * `slack`/`teams` channels remain in the union for back-compat with those
   * saved steps.
   */
  target: string;
  /** CC recipient(s) — comma-separated addresses; supports `@params.<name>`. */
  cc?: string;
  /** BCC recipient(s) — comma-separated addresses; supports `@params.<name>`. */
  bcc?: string;
  /** Email subject; supports `@params.<name>` interpolation. Falls back to the
   *  step title when empty. */
  subject?: string;
  /** Message body; supports `@params.<name>` interpolation like other step inputs. */
  message?: string;
}

export interface RunbookStepDTO {
  id: string;
  index: number;
  title: string;
  instruction: string;
  kind: RunbookStepKind;
  runner?: RunbookStepRunner;
  descriptionMarkdown?: string;
  inputs?: Record<string, StepInput>;
  outputs?: Record<string, RunbookStepOutputDTO>;
  actionLabels: string[];
  outputVarNames: string[];
  requiresHumanAction: boolean;
  /**
   * RFC-167 §13.5 (SOP Procedures): functional role that owns this step
   * (resolved against `ComplianceRole`). At run time the executor resolves
   * role → earliest eligible holder for human-gated steps. On an automated
   * step the same role becomes the owner of a remediation finding only after final failure.
   * Free-string vocabulary — roles are logical task groupings (HR / IT /
   * Accounting / Security Lead), not org-chart RBAC.
   */
  assignedRole?: string;
  /**
   * RFC-187 (decision A): the `ComplianceRole.id` this step is assigned to —
   * the rename-safe FK that supersedes the free-text `assignedRole`. When set,
   * the executor resolves it to the ComplianceRole's earliest eligible holder
   * ahead of the legacy name match. For an automated step this resolves the failure owner.
   * `assignedRole` is kept in sync as the display label.
   */
  assignedRoleId?: string;
  /**
   * A specific person (User id) this step is assigned to — ad-hoc, one-off
   * ownership ("Jane reviews this" / "Jane owns an automation failure"), as
   * opposed to a durable role/group. The executor resolves it to that user
   * ahead of role/group. Least resilient
   * option: if the user is inactive/removed at run time the executor treats it
   * as unresolved and escalates to admins (so a departed employee can't
   * silently block a step). Mutually exclusive with role/group/from;
   * `assignedRole` holds the person's display name as the label.
   */
  assignedUserId?: string;
  /**
   * RFC-187 (decisions B/C): a system-defined dynamic assignment group token
   * (e.g. 'active_employees', 'offboarding_employees'). The human-gated step
   * stays unassigned (no single `assigneeUserId`); every resolved group member
   * is notified and any one of them can claim / complete it. Mutually
   * exclusive with a role; `assignedRole` holds the group's display label.
   * Automated findings have a scalar owner, so a persisted automated group
   * assignment leaves the ticket owner unresolved.
   */
  assignedGroup?: string;
  /**
   * Dynamic assignee resolved from a runtime input at run time, rather than a
   * static role/group. Supported form:
   *   `@params.<paramName>.manager` — route the step to the *manager of the
   *   employee* identified by the email value of `<paramName>` (resolved via
   *   `Employee.managerEmployee`). Used for e.g. an access-request approval that
   *   must go to the requesting employee's own manager, not a fixed approver.
   * Mutually exclusive with `assignedRole`/`assignedRoleId`/`assignedGroup`. If
   * it can't resolve (no employee / no manager / manager has no user), the step
   * parks UNASSIGNED (to admins) rather than falling back to the run executor —
   * never auto-route an approval to the requester.
   */
  assignedFrom?: string
  /** RFC-210 request-aware selector. SUBJECT_MANAGER resolves exclusively from
   * the frozen requestContextSnapshot and never falls back to the requester. */
  assignmentSelector?: 'SUBJECT_MANAGER'
  /**
   * #543 — per-person fan-out mode for a GROUP-assigned human step:
   *  - 'ANY' (default): single-claim — any one member completing finishes the
   *    step (today's behavior; omit the field to get this).
   *  - 'ALL': population task — EVERY member of the group at launch must
   *    complete their own action (e.g. "every active employee acknowledges the
   *    handbook"); the step stays AWAITING_HUMAN until the last one is done.
   * Only meaningful when `assignedGroup` is set (a role resolves to a single
   * assignee, so fan-out doesn't apply).
   */
  fanout?: 'ANY' | 'ALL';
  /**
   * Relative deadline in days. Human-gated steps count from the moment the step
   * becomes ready, materialize onto `StepRun.dueAt`, and feed the per-step
   * overdue sweep. Automated steps use this as a business-day override for the
   * RemediationTicket SLA created after final failure. Distinct from CES-level
   * retest cadence (RFC-189), which governs when the whole procedure reopens.
   */
  dueOffsetDays?: number;
  /**
   * Soft per-step edit lock (UI-level, like the runbook-level
   * `RunbookDefinitionDTO.locked`). A locked step opens read-only in the
   * inspector, can't be dragged/deleted on the canvas, and shows a lock badge —
   * a guard against accidental edits, not a permission boundary. Unlock to edit.
   */
  locked?: boolean;
  automated?: RunbookAutomatedStepPayloadDTO;
  notify?: RunbookNotifyStepPayloadDTO;
  /** The consolidated human-step form (`kind: 'human'`). */
  human?: RunbookHumanFormDTO
  /** RFC-210 provider-neutral desired-state operation fulfilled through this
   * human step. Values may use the normal @params.* interpolation contract. */
  complianceOperation?: {
    operationKey:
      | 'identity.account.ensure'
      | 'identity.group_membership.ensure'
      | 'identity.account.suspend'
    provider: string
    connectionId?: string | null
    subjectExternalId?: string
    targetExternalId?: string
    desiredState: Record<string, StepInput>
    reason: string
    verificationMode?: 'provider_read' | 'artifact'
    verifierGroup?: string
  }
}

export interface RunbookUsageDTO {
  id: string;
  title: string;
  taskCode?: string | null;
  status: string;
  assignedTo?: {
    id: string;
    name?: string | null;
    email?: string | null;
  } | null;
}

export interface RunbookDefinitionDTO {
  id: string;
  workspaceId: string | null;
  title: string;
  description?: string | null;
  /**
   * RFC-206 §5.1 — natural-language routing statement; non-null + published ⇒
   * this runbook is a triage candidate (managed task.triage trigger enabled).
   */
  conditions?: string | null;
  /**
   * RFC-206 §5.5 opt-in — Vera may auto-start this procedure on a
   * HIGH-confidence free-text match without human confirmation. Default off.
   */
  autoTriageFreeText?: boolean;
  templateType: string;
  documentBody?: unknown | null;
  instructionsMarkdown: string;
  params: Record<string, RunbookParamDTO>;
  steps: RunbookStepDTO[];
  dependencies: RunbookDependencyDTO[];
  /** RFC — author-declared runbook outputs (the typed return signature). */
  outputs: RunbookOutputDeclarationDTO[];
  runbookKind: RunbookKind;
  runbookCategory: RunbookCategory;
  viewPreference?: 'document' | 'graph' | null;
  isPublic: boolean;
  /**
   * Soft edit-lock (stored in Workflow.metadata.locked). Locked runbooks open
   * read-only; unlocked ones open editable by default. UI-level guard, not a
   * permission boundary.
   */
  locked: boolean;
  currentVersionId?: string | null;
  controlMappings: Array<{
    id: string;
    isPrimary?: boolean;
    controlDefId: string;
    controlDef?: {
      controlId?: string;
      title?: string;
      framework?: {
        id?: string;
        name?: string;
      };
    } | null;
  }>;
  usedByTests: RunbookUsageDTO[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Completion of a consolidated `human` step. `values` holds each captured value
 * keyed by output name: a `text` field → string, a `files` field → artifact
 * id[], the decision → 'approve' | 'reject' (keyed by `decision.id`), and the
 * decision reason → string (keyed by `<decision.id>_reason`). `decision`
 * mirrors the chosen outcome (when the form has a gate) so the executor can
 * apply the reject→failure-edge gate without re-deriving it.
 */
export interface RunbookHumanCompletionDTO {
  kind: 'human';
  action: 'submit';
  values: Record<string, string | string[]>;
  decision?: 'approve' | 'reject';
  notes?: string;
  completedAt: string;
}

export type RunbookStepCompletionDTO = RunbookHumanCompletionDTO;

/** Durable, identity-bound record produced by non-empty `sign: true` fields. */
export interface RunbookStepAttestationDTO {
  version: 1;
  signer: {
    userId: string;
    name: string;
  };
  signedAt: string;
  statements: Array<{
    fieldId: string;
    label: string;
    text: string;
  }>;
}

export interface StepRunDTO {
  id: string;
  workflowExecutionId: string;
  stepId: string;
  stepKind: RunbookStepKind;
  runner?: RunbookStepRunner | null;
  status: StepRunStatus;
  startedAt?: string | null;
  finishedAt?: string | null;
  readyAt?: string | null;
  upstreamStepIds?: string[];
  blockedByStepIds?: string[];
  resolvedInputs: Record<string, unknown>;
  outputs?: Record<string, unknown> | null;
  completion?: RunbookStepCompletionDTO | null;
  error?: Record<string, unknown> | null;
  artifactIds: string[];
  assigneeUserId?: string | null;
  assigneeRole?: string | null;
  dueAt?: string | null;
  completedByUserId?: string | null;
  completedAt?: string | null;
  attestationSignature?: RunbookStepAttestationDTO | null;
}

export interface WorkflowOutputDTO {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  description: string;
  extractedFrom?: string; // Step ID
}

/**
 * @deprecated Use `RunbookParamDTO` (RFC-167 §5.7.2). Kept as an alias so
 * the legacy workflow-editor surfaces (`WorkflowEditor.tsx`,
 * `ParameterPanel.tsx`, etc.) continue to typecheck during the unification
 * window; will be removed once those surfaces are deleted.
 */
export type WorkflowParamDTO = RunbookParamDTO;

export type ParamType =
  | 'string'
  | 'text'
  | 'number'
  | 'ordinal'
  | 'boolean'
  | 'url'
  | 'email'
  | 'date'
  | 'enum'
  | 'json'
  | 'files'
  // Run-identity input (RFC-167): the logged-in browser profile a `runner:browser`
  // procedure runs under. Program-level (one per run) — NOT referenced by steps
  // as `@params.x`; the run dialog renders it via <BrowserProfileSelector> and
  // maps its value to `runbook.execute({ browserProfileId })`.
  | 'browserProfile';

export interface ParamValidationDTO {
  pattern?: string; // Regex pattern
  min?: number; // Min value (for numbers)
  max?: number; // Max value (for numbers)
  enum?: string[]; // Allowed values
}

export interface WorkflowMetadataDTO {
  // Compilation info
  compiledAt: string;
  compiledBy: string; // "gpt-4o" | "claude-3.5-sonnet" | "human"
  compilerVersion: string; // "1.0.0"
  schemaVersion: number; // Workflow schema version

  // Quality metrics
  confidence?: number; // 0.0-1.0
  accuracy?: number; // 0.0-1.0
  editedSteps?: number;

  // Source info
  sourceDocId?: string;
  sourceDocVersion?: number; // Doc version at compilation
  sourceDocHash?: string; // SHA-256 hash
  sourceStepCount: number;

  // Usage stats
  executionCount?: number;
  successRate?: number;
  avgCost?: number;
  fastPathRate?: number;

  // Learning
  adaptationCount?: number;
  lastAdaptedAt?: string;
  adaptationSuccessRate?: number;
}

// ========== Workflow Documentation ==========

export interface WorkflowDocumentationDTO {
  markdown: string; // Complete documentation in markdown
  title: string;
  summary?: string;
  tags?: string[];
  stepDocs?: StepDocumentationDTO[];

  sourceDocId?: string;
  sourceUrl?: string;

  generatedAt?: string;
  generatedBy?: string;
}

export interface StepDocumentationDTO {
  stepId: string;

  title: string;
  description?: string;

  guideAction?: string;
  intent?: string;

  screenshot?: string;
  screenshotCaption?: string;

  tips?: string[];
  warnings?: string[];

  recordedAt?: string;
  url?: string;
}

// ========== Workflow Steps ==========

export interface WorkflowStepDTO {
  id: string;
  intent: string;
  action: StepAction;
  prompt: string;
  hints: StepHintsDTO;
  output?: string; // Legacy: single output name (deprecated, use extractions)
  extractions?: StepExtractionDTO[]; // NEW: Multiple extractions per step
  condition?: StepConditionDTO;
  retry?: RetryConfigDTO;
  metadata?: Record<string, any>;

  // Natural language success criteria
  successCriteria?: string; // e.g., "login should fail", "extract the title"

  // Recording signals
  observedState?: ObservedStateDTO;

  // Optional validation policy (intent + signal contract)
  validation?: StepValidationPolicyDTO;

  // Pagination configuration (RFC-103)
  pagination?: StepPaginationConfigDTO;

  // Selector strategies from recording (for Fast Path execution)
  selectorStrategies?: SelectorStrategiesDTO;
}

// ========== Pagination Configuration (RFC-103) ==========

/**
 * Pagination configuration for workflow steps.
 * Enables automatic capture of multiple pages during workflow execution.
 */
export interface StepPaginationConfigDTO {
  enabled: boolean;

  /** Navigation method: 'url' for direct URL navigation, 'selector' for clicking next button */
  method: 'url' | 'selector';

  /** URL-based pagination configuration (preferred when available) */
  urlConfig?: PaginationUrlConfigDTO;

  /** Selector-based pagination configuration (fallback) */
  selectorConfig?: PaginationSelectorConfigDTO;

  /** Capture strategy */
  strategy: 'sample' | 'firstLast' | 'all';

  /** Strategy-specific configuration for 'sample' strategy */
  sampleConfig?: PaginationSampleConfigDTO;

  /** Strategy-specific configuration for 'firstLast' strategy */
  firstLastConfig?: PaginationFirstLastConfigDTO;

  /** Strategy-specific configuration for 'all' strategy */
  allConfig?: PaginationAllConfigDTO;
}

/** URL-based pagination configuration */
export interface PaginationUrlConfigDTO {
  /** URL pattern with {page} placeholder, e.g., "https://app.com/users?page={page}" */
  pattern: string;
  /** Starting page number (usually 1, sometimes 0) */
  startPage: number;
}

/** Selector-based pagination configuration */
export interface PaginationSelectorConfigDTO {
  /** CSS selector for the "next" button */
  nextSelector: string;
  /** Time to wait after clicking next (ms), default 1500 */
  waitAfterClick: number;
}

/** Sample strategy configuration */
export interface PaginationSampleConfigDTO {
  /** Hard limit on total pages to capture, default 10 */
  maxPages: number;
  /** Number of first pages to capture, default 2 */
  captureFirst: number;
  /** Number of last pages to capture, default 2 */
  captureLast: number;
  /** Number of evenly-spaced middle pages to sample, default 3 */
  sampleMiddle: number;
}

/** First/Last strategy configuration */
export interface PaginationFirstLastConfigDTO {
  /** Number of first pages to capture, default 1 */
  captureFirst: number;
  /** Number of last pages to capture, default 1 */
  captureLast: number;
  /** Safety cap on total pages, default 10 */
  maxPages: number;
}

/** All pages strategy configuration */
export interface PaginationAllConfigDTO {
  /** Safety cap on max pages, default 20 */
  maxPages: number;
}

// NEW: Define what to extract and where to store it
export interface StepExtractionDTO {
  name: string; // Output variable name (e.g., "issueTitle", "os")
  selector?: string; // Specific selector for this extraction (overrides step hints)
  xpath?: string;
  extractField?: ExtractField; // What property to extract
  description?: string; // What this extraction represents
}

export type StepAction =
  | 'navigate'
  | 'click'
  | 'type'
  | 'extract'
  | 'wait'
  | 'scroll'
  | 'hover'
  | 'select'
  | 'upload'
  | 'download'
  | 'screenshot';

export interface StepHintsDTO {
  // Selector strategies
  selector?: string;
  xpath?: string;
  testId?: string;
  ariaLabel?: string;

  // Element identification
  domText?: string;
  ordinality?: string; // "first" | "last" | "2" | "@position"
  containerSelector?: string; // NEW: Container constraint for performance

  // Validation
  fingerprint?: WorkflowElementFingerprintDTO;

  // Navigation hints
  url?: string;
  urlPattern?: string;

  // Type hints
  value?: string;
  clearFirst?: boolean;
  pressEnter?: boolean;

  // Extract hints
  extractField?: ExtractField;
  extractPattern?: string;
  extractAll?: boolean; // NEW: Extract from all matching elements (returns array)
  extractLimit?: number; // NEW: Limit number of items to extract (default: 50)
  extractTable?: boolean; // NEW: Extract table as structured data (2D array or objects)
  tableFormat?: 'array' | 'objects'; // NEW: Return format (default: 'objects')
  tableHeaderRow?: number; // NEW: Which row contains headers (default: 0)

  // Wait hints
  waitFor?: WaitConditionDTO;
  waitAfter?: WaitConditionDTO;
  timeout?: number;

  // Visual hints
  screenshot?: string;
  boundingBox?: BoundingBoxDTO;
}

export interface WorkflowElementFingerprintDTO {
  id?: string;
  href?: string;
  textContent?: string;
  tagName?: string;
  classList?: string[];
  attributes?: Record<string, string>;
}

export type ExtractField =
  | 'textContent'
  | 'value'
  | 'href'
  | 'src'
  | 'innerText'
  | 'innerHTML'
  | 'attribute';

export interface WaitConditionDTO {
  type: 'url' | 'element' | 'text' | 'networkIdle';
  value?: string;
  timeout?: number;
}

export interface BoundingBoxDTO {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface StepConditionDTO {
  type: 'if' | 'unless';
  check: ConditionCheckDTO;
  skipIfFalse?: boolean;
  errorIfFalse?: boolean;
}

export interface ConditionCheckDTO {
  type: 'elementExists' | 'elementNotExists' | 'urlMatches' | 'variableEquals';
  selector?: string;
  urlPattern?: string;
  variable?: string;
  value?: any;
}

export interface RetryConfigDTO {
  maxAttempts: number;
  delay?: number;
  backoff?: 'linear' | 'exponential';
}

// ========== Observed State ==========

export interface ObservedStateDTO {
  // Error elements detected during recording
  errorElements?: Array<{
    text: string;
    selector: string;
    color?: string;
    role?: string;
  }>;

  // Behavioral signals
  urlChanged?: boolean;
  modalAppeared?: boolean;
  httpStatus?: number;
}

export type StepValidationMode = 'auto' | 'dom' | 'visual' | 'hybrid';

export type StepIntentType =
  | 'focus_input'
  | 'open_panel'
  | 'close_panel'
  | 'toggle_on'
  | 'toggle_off'
  | 'submit_form'
  | 'navigate'
  | 'observe_only'
  | 'input_value'
  | 'select_option'
  | 'generic_click';

export type StepValidationStrictness = 'strict' | 'balanced' | 'lenient';
export type StepAuthFlow = 'none' | 'login' | 'logout' | 'switch_user' | 'unknown';
export type SessionExpiryCheckMode = 'auto' | 'strict' | 'off';
export type StepAuthFlowSource = 'llm' | 'heuristic' | 'manual';

export interface StepValidationPolicyDTO {
  mode?: StepValidationMode;
  intentType?: StepIntentType;
  authFlow?: StepAuthFlow;
  sessionExpiryCheck?: SessionExpiryCheckMode;
  authFlowSource?: StepAuthFlowSource;
  authFlowConfidence?: number;
  requiredSignals?: string[];
  optionalSignals?: string[];
  failureSignals?: string[];
  strictness?: StepValidationStrictness;
}

// ========== Execution Results ==========

export interface ExecutionResultDTO {
  workflowId: string;
  executedAt: string;
  params: Record<string, any>;
  outputs: Record<string, any>;

  results: StepResultDTO[];
  screenshots: string[];

  // Metrics
  totalCost: number;
  successRate: number;
  fastPathRate: number;
  executionTime: number;

  // Evidence pack
  evidencePack?: EvidencePackDTO;
}

export interface StepResultDTO {
  stepId: string;
  intent?: string; // Human-readable step name for UI display
  success: boolean;
  skipped?: boolean; // True if step was skipped due to previous step failure
  method: 'fast_path' | 'llm_adapted' | 'error' | 'recording' | 'observe' | 'vision' | 'llm' | 'native'; // Execution method / DeepGuide layer
  action?: StepAction; // Action type: click, navigate, type, extract, etc.

  // Fast path details
  selectorUsed?: string;
  fingerprintScore?: number;

  // Adaptive path details
  adaptedSelector?: string;
  confidence?: number;

  // Extraction
  extractedValue?: any; // Legacy: single extracted value
  extractedValues?: Record<string, any>; // NEW: Multiple extracted values by name
  actionCompleted?: boolean;

  // Timing
  startTime?: string;
  endTime?: string;
  duration?: number; // Step duration in milliseconds

  // RFC-087: Enhanced tracking fields
  screenshotUrl?: string; // Direct URL to screenshot in S3/R2

  // RFC-087: Self-healing selector tracking
  selectorHealed?: boolean; // Was selector automatically updated?
  originalSelector?: string; // Original recorded selector
  healedSelector?: string; // New selector found by observe/vision/llm
  healingMethod?: 'observe' | 'vision' | 'llm'; // Which layer healed it
  healingConfidence?: number; // 0-100 confidence score

  // Error handling
  error?: string;
  currentUrl?: string;     // URL where the step failed
  pageTitle?: string;      // Page title at failure time
  reason?: string;
  retries?: number;
}

export interface EvidencePackDTO {
  id: string;
  workflowId: string;
  execution: ExecutionResultDTO;
  attestation: AttestationDTO;
  generatedAt: string;
}

export interface AttestationDTO {
  signature: string;
  algorithm: string;
}

// ========== Browser Profile ==========

export interface BrowserProfileDTO {
  id: string;
  workspaceId: string;
  name: string;

  // Workflow-specific fields (optional)
  appName?: string;
  environment?: 'test' | 'production';

  // Compliance-specific fields (RFC-087)
  profileType?: string;  // Freeform: "Administrator", "Manager", "Auditor"
  email?: string;
  description?: string;

  loginUrl: string;

  // Storage (local or Steel)
  profilePath?: string;
  encryptionKeyId?: string;
  steelProfileId?: string;
  steelSessionId?: string;
  steelSessionUrl?: string;
  steelMetadata?: Record<string, unknown>;

  // Validation
  isValid: boolean;
  lastValidatedAt: string;
  lastValidatedBy?: string; // User ID if user-validated, null/undefined if system-validated
  validUntil?: string;
  userMarkedLoginIncomplete: boolean;

  // Timestamps
  createdAt: string;
  updatedAt: string;
  createdBy?: string;

  // Relations (included when requested)
  creator?: {
    id: string;
    name?: string;
    email?: string;
  };

  // Usage stats (included when requested)
  _count?: {
    scheduledExecutions?: number;
    executions?: number;
  };

  latestExecution?: {
    id: string;
    workflowId: string;
    status: string;
    executedAt: string;
    successRate?: number;
  };
}

// ========== Workflow Execution DTO ==========

export interface WorkflowExecutionDTO {
  id: string;
  workflowId: string;
  params: Record<string, any>;
  outputs: Record<string, any>;
  results: StepResultDTO[];
  screenshots: string[];
  totalCost: number;
  successRate: number;
  fastPathRate: number;
  executionTime: number;
  workflowValidation?: boolean; // Whether workflow-level assertions/outputs validation passed
  validationDetails?: {
    missingOutputs?: string[];
    failedAssertions?: string[];
    sessionExpiryVerdict?: {
      signalCount?: number;
      confirmedCount?: number;
      suppressedAuthSignalCount?: number;
      lastSignalStep?: number;
      lastAuthFlow?: StepAuthFlow;
    };
  };
  evidencePack?: EvidencePackDTO;
  executedAt: string;
  executedBy: string;
  failedType?: WorkflowExecutionFailedType;

  // RFC-087: Browser session replay (provider-agnostic)
  sessionProvider?: string; // 'steel' | 'browserbase' | 'playwright-cloud' | etc
  sessionId?: string;
  sessionUrl?: string;
  sessionMetadata?: Record<string, any>; // Optional: provider-specific data
}

// ========== Workflow Execution Progress ==========

export interface WorkflowExecutionProgressDTO {
  executionId: string;
  workflowId: string;
  status: 'initializing' | 'running' | 'completed' | 'failed' | 'cancelled';
  currentStep: number; // 0-indexed
  totalSteps: number;
  currentStepName?: string;
  currentStepStatus?: 'pending' | 'running' | 'completed' | 'failed';

  // Step results so far
  completedSteps: number;
  failedSteps: number;

  // Workflow validation status (only available at completion)
  workflowValidation?: boolean;
  validationDetails?: {
    missingOutputs?: string[];
    failedAssertions?: string[];
    sessionExpiryVerdict?: {
      signalCount?: number;
      confirmedCount?: number;
      suppressedAuthSignalCount?: number;
      lastSignalStep?: number;
      lastAuthFlow?: StepAuthFlow;
    };
  };
  successRate: number;

  // Timing
  startedAt: string;
  estimatedCompletion?: string;
  elapsedTime: number; // milliseconds

  // Latest screenshot (for live monitoring)
  latestScreenshot?: string;

  // Steel.dev session viewer URL (if using cloud browser)
  sessionViewerUrl?: string;

  // Steel session lifecycle
  sessionReleased?: boolean;
  sessionReleaseAt?: string;
  replayReady?: boolean;
  recordingUnavailable?: boolean;

  // Error details (if failed)
  error?: string;
}

// ========== Global State & Batch Execution (RFC-084) ==========

/// WorkflowExecutionContext - Persistent state across workflow executions
export interface WorkflowExecutionContextDTO {
  id: string;
  workspaceId: string;

  /// Context metadata
  name: string;
  description?: string;
  category: string; // "auth" | "compliance" | "bpo" | "testing" | "general"

  /// Global state (shared across executions)
  state: Record<string, any>;

  /// Security
  expiresAt?: string;
  isActive: boolean;

  /// Usage tracking
  usageCount: number;
  usedByWorkflows: string[];
  lastUsedAt: string;

  /// Audit trail
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

/// TestDataSet - Reusable test data for batch execution
export interface TestDataSetDTO {
  id: string;
  workspaceId: string;

  /// Metadata
  name: string;
  description?: string;
  category: string; // "compliance" | "bpo" | "qa" | "regression" | "testing"

  /// Data
  data: Record<string, any>[]; // Array of test records
  schema?: {
    fields: Array<{
      name: string;
      type: string;
      required?: boolean;
    }>;
  };

  /// Source information
  sourceType?: string; // "csv" | "api" | "manual" | "integration"
  sourceFile?: string;
  sourceUrl?: string;

  /// Versioning (for compliance audit trail)
  version: number;
  hash: string; // SHA-256 hash to detect tampering

  /// Compliance metadata
  controlId?: string; // SOC2/GDPR control (e.g., "CC6.1")
  auditPeriod?: string; // "Q1-2025"

  /// Statistics
  recordCount: number;
  executionCount: number;
  usedInWorkflows: string[];

  /// Audit trail
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

/// BatchExecution - Bulk workflow execution against test data set
export interface BatchExecutionDTO {
  id: string;
  workflowId: string;
  dataSetId: string;
  workspaceId: string;

  /// Configuration
  concurrency: number;
  headless: boolean;
  timeout?: number;
  contextId?: string; // WorkflowExecutionContext ID for shared state

  /// Status
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

  /// Progress metrics
  totalRecords: number;
  processedRecords: number;
  successCount: number;
  failureCount: number;

  /// Results
  results?: {
    executions: Array<{
      recordId: number;
      executionId: string;
      status: 'success' | 'failed';
      duration: number;
      error?: string;
    }>;
    summary: {
      totalCost: number;
      avgDuration: number;
      fastPathRate: number;
    };
  };

  /// Timing
  startedAt?: string;
  completedAt?: string;
  duration?: number;

  /// Compliance metadata
  reviewedBy?: string;
  reviewedAt?: string;
  approved: boolean;

  /// Audit trail
  createdAt: string;
  createdBy: string;

  /// Relationships (optional)
  workflow?: {
    id: string;
    name: string;
  };
  dataSet?: {
    id: string;
    name: string;
    recordCount: number;
  };
}

/// BatchExecutionProgressDTO - Real-time progress update for batch execution
export interface BatchExecutionProgressDTO {
  batchId: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

  /// Progress
  totalRecords: number;
  processedRecords: number;
  currentRecord?: number;

  /// Metrics
  successCount: number;
  failureCount: number;

  /// Timing
  startedAt: string;
  estimatedCompletion?: string;
  elapsedTime: number; // milliseconds

  /// Latest execution
  latestExecution?: {
    recordId: number;
    executionId: string;
    status: 'success' | 'failed';
  };

  /// Error details (if failed)
  error?: string;
}
